import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export type ToastVariant = 'success' | 'error';

export interface ToastMessage {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  toasts: ToastMessage[];
  showToast: (message: string, variant?: ToastVariant) => void;
  dismissToast: (id: number) => void;
}

/** How long a toast stays up before it dismisses itself. */
const TOAST_DURATION_MS = 5000;

export const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * `useToast` is the surface components call to trigger a toast (FR-004/005/
 * 006/008/012/017's exact copy) — it deliberately exposes only `showToast`,
 * not the toast list or dismiss mechanics, which belong to `ToastProvider`.
 *
 * `variant` defaults to 'success' (sc-43661's original green toast, still
 * every existing call site). 'error' is the red toast added for sc-45103's
 * 1-key floor (Figma 3087:15007's "web-toaster", Danger/rd-400 #DE2C2C) —
 * "You need a second key before you can delete this one" reads as a
 * blocked action, not a confirmation, so it gets the error treatment
 * rather than the green one every other toast in this app uses.
 */
export const useToast = (): Pick<ToastContextValue, 'showToast'> => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return { showToast: context.showToast };
};

export const useToastState = (): ToastContextValue => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextId = useRef(0);
  // Tracks each toast's auto-dismiss timer so a manual dismiss (or provider
  // unmount) can cancel it — otherwise a stale dismissToast(id) can still
  // fire later for a toast that's already gone.
  const timers = useRef(new Map<number, number>());

  const dismissToast = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, variant: ToastVariant = 'success') => {
      const id = nextId.current;
      nextId.current += 1;
      setToasts((current) => [...current, { id, message, variant }]);
      timers.current.set(
        id,
        window.setTimeout(() => dismissToast(id), TOAST_DURATION_MS)
      );
    },
    [dismissToast]
  );

  useEffect(() => {
    const timersOnUnmount = timers.current;
    return () => {
      timersOnUnmount.forEach((timer) => window.clearTimeout(timer));
      timersOnUnmount.clear();
    };
  }, []);

  return { toasts, showToast, dismissToast };
};

export default useToast;
